import { useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { X, Check, Trash2, Sparkles, ShoppingBag } from "lucide-react";
import { Button } from "./ui/button";
import { useCart } from "../contexts/CartContext";
import UpsellBanner from "./UpsellBanner";
import { getDisplayName, getDisplayImage } from "../utils/variationDisplay";

export default function CartConfirmation() {
  const { confirmation, dismissConfirmation, cartGroups, cartTotals, cartCount, discountInfo, fetchCart, removeFromCart } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    if (confirmation) {
      fetchCart();
      const timer = setTimeout(dismissConfirmation, 12000);
      return () => clearTimeout(timer);
    }
  }, [confirmation, dismissConfirmation, fetchCart]);

  const handleRemove = useCallback(async (itemId) => {
    await removeFromCart(itemId);
    await fetchCart();
  }, [removeFromCart, fetchCart]);

  if (!confirmation) return null;

  const discountPct = discountInfo?.discount_percent || 0;

  return (
    <>
      <div className="fixed inset-0 bg-black/15 z-[60]" onClick={dismissConfirmation} />
      <div
        className="fixed top-0 right-0 h-full w-full sm:w-[400px] bg-[#FAF8F0] z-[60] shadow-2xl flex flex-col"
        style={{ animation: "slideInRight 300ms cubic-bezier(0.16, 1, 0.3, 1) forwards" }}
        data-testid="cart-confirmation-panel"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#282828]/8 flex-shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-[#9A7B4F]/15 flex items-center justify-center">
              <Check className="w-3.5 h-3.5 text-[#9A7B4F]" strokeWidth={2} />
            </div>
            <span className="text-[13px] text-[#282828] font-medium">Your Cart</span>
            <span className="text-[10px] text-[#676664]/50 bg-[#282828]/5 px-2 py-0.5 rounded-full">{cartCount} {cartCount === 1 ? 'item' : 'items'}</span>
          </div>
          <button onClick={dismissConfirmation} className="p-1.5 text-[#676664]/40 hover:text-[#282828]" data-testid="cart-confirm-close">
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>

        {/* Discount badge */}
        {discountPct > 0 && (
          <div className="px-5 pt-3 flex-shrink-0">
            <div className="flex items-center gap-1.5 px-3 py-2 bg-[#F5F0E6] border border-[#9A7B4F]/15 text-[10px] text-[#9A7B4F]" data-testid="side-cart-discount-badge">
              <Sparkles className="w-3 h-3 flex-shrink-0" strokeWidth={1.5} />
              <span>{discountPct}% multi-item discount applied</span>
            </div>
          </div>
        )}

        {/* Cart items list */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {cartGroups.map(group => {
            const allItems = [group.main, ...group.addons];
            return allItems.map(item => {
              const tierDisc = item.tier_discount_amount || 0;
              const _displayName = getDisplayName(item, item);
              const _displayImage = getDisplayImage(item, item);
              return (
                <div key={item.id} className="flex gap-3 pb-4 border-b border-[#282828]/5 last:border-0" data-testid={`side-cart-item-${item.id}`}>
                  {_displayImage && (
                    <Link to={`/outfit/${item.outfit_id}`} onClick={dismissConfirmation} className="flex-shrink-0">
                      <div className="w-16 h-20 bg-[#f5f3f0] overflow-hidden">
                        <img src={_displayImage} alt={_displayName} className="w-full h-full object-cover" />
                      </div>
                    </Link>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-1">
                      <Link to={`/outfit/${item.outfit_id}`} onClick={dismissConfirmation}
                        className="text-[13px] text-[#1A1A1A] hover:text-[#9A7B4F] leading-snug truncate block"
                        style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
                        {_displayName}
                      </Link>
                      <button onClick={() => handleRemove(item.id)}
                        className="p-1 text-[#676664]/25 hover:text-red-400 transition-colors flex-shrink-0"
                        data-testid={`side-cart-remove-${item.id}`}>
                        <Trash2 className="w-3 h-3" strokeWidth={1.5} />
                      </button>
                    </div>
                    <div className="mt-1.5 space-y-0.5">
                      <div className="flex items-center gap-1.5 text-[11px]">
                        <span className="text-[#5A5A5A]">Rental</span>
                        {tierDisc > 0 && <span className="text-[#676664]/30 line-through text-[10px]">${item.rental_price.toFixed(2)}</span>}
                        <span className="text-[#282828] font-medium ml-auto">${(item.rental_price - tierDisc).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-[11px]">
                        <span className="text-[#5A5A5A]">Security Bond*</span>
                        <span className="text-[#282828] font-medium">${item.bond_amount.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            });
          })}
        </div>

        {/* Totals + Buttons */}
        <div className="px-5 pb-4 pt-3 border-t border-[#282828]/8 flex-shrink-0 space-y-3">
          {/* Upsell banner */}
          {discountInfo?.next_tier && <UpsellBanner variant="compact" />}

          {/* Totals summary */}
          <div className="space-y-1.5 text-[12px]">
            <div className="flex justify-between">
              <span className="text-[#5A5A5A]">Rental Total</span>
              <span className="text-[#282828]">${(cartTotals.rental || 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[#5A5A5A]">Security Bonds*</span>
              <span className="text-[#282828]">${(cartTotals.bond || 0).toFixed(2)}</span>
            </div>
            {(cartTotals.discount || 0) > 0 && (
              <div className="flex justify-between">
                <span className="text-[#9A7B4F]">Multi-Item Discount ({discountPct}%)</span>
                <span className="text-[#9A7B4F] font-medium">-${(cartTotals.discount || 0).toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between pt-2 border-t border-[#282828]/8">
              <span className="text-[#282828] font-medium text-[13px]">Total Due</span>
              <span className="text-[#9A7B4F] font-semibold text-[15px]">${(cartTotals.grand_total || 0).toFixed(2)}</span>
            </div>
          </div>

          {/* CTA Buttons */}
          <Button
            onClick={() => { dismissConfirmation(); navigate("/cart"); }}
            className="w-full bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white py-4 text-[10px] tracking-[0.15em] uppercase"
            data-testid="cart-confirm-view-cart"
          >
            View Cart
          </Button>
          <Button
            onClick={dismissConfirmation}
            variant="outline"
            className="w-full border-[#282828]/15 text-[#676664] py-3 text-[10px] tracking-[0.12em] uppercase hover:border-[#9A7B4F]/30"
            data-testid="cart-confirm-continue"
          >
            Continue Browsing
          </Button>
        </div>
      </div>

      <style>{`
        @keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }
      `}</style>
    </>
  );
}
